/**
 * Inversion model parser — EarthImager 2D exports (.xyz/.dat), RES2DINV .inv,
 * and generic delimited x/z/resistivity tables.
 *
 * Normalized model:
 * {
 *   name, format, nCells,
 *   columns: ['x','depth','resistivity', 'chargeability'?, 'sensitivity'?, 'misfit'?],
 *   cells: [{ x, depth, resistivity, chargeability?, sensitivity?, misfit? }],
 *   xRange: [min,max], depthRange: [min,max],
 *   depths: number[] (unique sorted depth levels),
 *   rmsPercent: number|null (global misfit from header if found),
 *   doi: number (depth of investigation estimate),
 * }
 *
 * Conventions: depth is positive-down meters. EarthImager exports negative
 * elevations/depths — normalized here. Resistivity in Ωm.
 */

const HEADER_ROLES = [
  { role: 'x', patterns: [/^(x|dist|distance|station|position)$/i, /dist|station|position|(^|_)x($|_)/i] },
  { role: 'depth', patterns: [/^(z|depth|elev|elevation)$/i, /depth|elev|(^|_)z($|_)/i] },
  { role: 'resistivity', patterns: [/^(res|rho|resistivity)$/i, /resist|rho|ohm/i] },
  { role: 'chargeability', patterns: [/^(ip|charge|chargeability|m)$/i, /charge|(^|_)ip($|_)/i] },
  { role: 'sensitivity', patterns: [/^(sens|sensitivity|coverage)$/i, /sens|coverage/i] },
  { role: 'misfit', patterns: [/^(misfit|rms|error)$/i, /misfit|rms|error/i] },
];

function isNum(v) {
  const n = parseFloat(v);
  return !isNaN(n) && isFinite(n);
}

function splitCols(line) {
  return line.trim().split(/[,;\t]+|\s+/).filter(Boolean);
}

/**
 * Parse inversion file text into a normalized model.
 * @param {string} text - raw file content
 * @param {string} fileName - used for format hints (.inv/.xyz/.dat)
 * @returns normalized model (throws on failure)
 */
export function parseInversionFile(text, fileName = '') {
  const ext = (fileName.split('.').pop() || '').toLowerCase();
  const lines = text.split(/\r?\n/);

  // Global RMS from header comments (EarthImager + RES2DINV both mention it)
  let rmsPercent = null;
  for (const line of lines.slice(0, 60)) {
    const m = line.match(/(?:rms|misfit)[^0-9]*([\d.]+)\s*%/i);
    if (m) { rmsPercent = parseFloat(m[1]); break; }
  }

  // Per-iteration convergence history (EarthImager .out/.inv logs:
  // "Iteration 3 ... RMS = 4.21 %" or "Iter  3   RMS: 4.21")
  const iterations = [];
  for (const line of lines) {
    const m = line.match(/iter(?:ation)?\s*[#=:]?\s*(\d+)\b[^0-9]*(?:rms|misfit)[^0-9]*([\d.]+)/i);
    if (m) {
      const n = parseInt(m[1]);
      const rms = parseFloat(m[2]);
      if (isFinite(n) && isFinite(rms) && !iterations.some(it => it.n === n)) {
        iterations.push({ n, rms });
      }
    }
  }
  iterations.sort((a, b) => a.n - b.n);
  if (rmsPercent === null && iterations.length > 0) {
    rmsPercent = iterations[iterations.length - 1].rms;
  }

  // Locate header row (if any) and numeric data rows
  let headerCols = null;
  const dataRows = [];
  for (const raw of lines) {
    const line = raw.trim();
    if (!line || line.startsWith('#') || line.startsWith('//') || line.startsWith(';')) continue;
    const cols = splitCols(line);
    if (cols.length < 3) continue;
    const numericCount = cols.filter(isNum).length;
    if (numericCount >= 3 && numericCount >= cols.length - 1) {
      dataRows.push(cols.map(parseFloat));
    } else if (!headerCols && dataRows.length === 0 && numericCount < cols.length / 2) {
      headerCols = cols; // last non-numeric row before data
    }
  }
  if (dataRows.length < 10) {
    throw new Error(`Only ${dataRows.length} numeric rows found — not a recognizable inversion export`);
  }

  // Keep only the dominant column count (drops electrode tables etc. in .inv files)
  const countFreq = {};
  dataRows.forEach(r => { countFreq[r.length] = (countFreq[r.length] || 0) + 1; });
  const domCount = parseInt(Object.entries(countFreq).sort((a, b) => b[1] - a[1])[0][0]);
  const rows = dataRows.filter(r => r.length === domCount);

  // Assign column roles
  const roles = new Array(domCount).fill(null);
  if (headerCols && headerCols.length === domCount) {
    HEADER_ROLES.forEach(({ role, patterns }) => {
      if (roles.includes(role)) return;
      for (const pat of patterns) {
        const idx = headerCols.findIndex((h, i) => !roles[i] && pat.test(h));
        if (idx !== -1) { roles[idx] = role; return; }
      }
    });
  }
  const headerResistivity = roles.includes('resistivity');
  // Positional fallback: x, z, resistivity, (chargeability), (sensitivity)
  const order = ['x', 'depth', 'resistivity', 'chargeability', 'sensitivity'];
  let next = 0;
  for (let i = 0; i < domCount; i++) {
    if (roles[i]) continue;
    while (next < order.length && roles.includes(order[next])) next++;
    roles[i] = next < order.length ? order[next++] : `col_${i}`;
  }

  const xi = roles.indexOf('x');
  const zi = roles.indexOf('depth');
  const ri = roles.indexOf('resistivity');
  if (xi === -1 || zi === -1 || ri === -1) {
    throw new Error('Could not identify x / depth / resistivity columns');
  }

  // Depth normalization: negative z → depth = -z (EarthImager elevation convention)
  const zVals = rows.map(r => r[zi]);
  const zMax = Math.max(...zVals.slice(0, 5000));
  const zMin = Math.min(...zVals.slice(0, 5000));
  let toDepth;
  if (zMax <= 0) {
    toDepth = (z) => -z;               // all negative → below-surface elevations
  } else if (zMin >= 0) {
    toDepth = (z) => z;                // all positive → already depths
  } else {
    const surface = zMax;              // mixed → elevations with topo; depth from max
    toDepth = (z) => surface - z;
  }

  const cells = rows.map(r => {
    const cell = { x: r[xi], depth: Math.round(toDepth(r[zi]) * 100) / 100, resistivity: r[ri] };
    roles.forEach((role, i) => {
      if (['x', 'depth', 'resistivity'].includes(role) || role.startsWith('col_')) return;
      cell[role] = r[i];
    });
    return cell;
  }).filter(c => isNum(c.resistivity) && c.resistivity > 0);

  if (cells.length < 10) throw new Error('No valid resistivity cells after parsing');

  const xs = cells.map(c => c.x);
  const depths = cells.map(c => c.depth);
  const uniqueDepths = [...new Set(depths.map(d => Math.round(d * 10) / 10))].sort((a, b) => a - b);
  const maxDepth = Math.max(...depths.slice(0, 100000));

  // DOI: sensitivity-based when available, else 0.75 × max model depth
  let doi = maxDepth * 0.75;
  if (roles.includes('sensitivity')) {
    const sens = cells.map(c => c.sensitivity).filter(isNum).sort((a, b) => a - b);
    if (sens.length > 10) {
      const threshold = sens[Math.floor(sens.length * 0.1)]; // bottom decile
      const deepReliable = cells.filter(c => c.sensitivity > threshold).map(c => c.depth);
      if (deepReliable.length) doi = Math.max(...deepReliable);
    }
  }

  const columns = ['x', 'depth', 'resistivity',
    ...['chargeability', 'sensitivity', 'misfit'].filter(r => roles.includes(r))];

  return {
    name: fileName.replace(/\.[^.]+$/, '') || 'Inversion model',
    format: ext === 'inv' ? 'RES2DINV/EarthImager .inv' : ext === 'xyz' ? 'XYZ export' : `.${ext}`,
    nCells: cells.length,
    columns,
    cells,
    xRange: [Math.min(...xs.slice(0, 100000)), Math.max(...xs.slice(0, 100000))],
    depthRange: [Math.min(...depths.slice(0, 100000)), maxDepth],
    depths: uniqueDepths,
    rmsPercent,
    iterations,
    headerResistivity,
    doi: Math.round(doi * 100) / 100,
  };
}

/** More distinct depth levels than any real 2D section (EarthImager/RES2DINV use tens). */
export const MAX_PLAUSIBLE_DEPTH_LEVELS = 200;

/**
 * Warning text when a parsed model looks like some other table (a point cloud / survey CSV read
 * by the lenient positional parser), else null.
 */
export function implausibleModelWarning(model, fileName = '') {
  if (!model) return null;
  const levels = (model.depths || []).length;
  const csvLike = /\.(csv|txt|xyz)$/i.test(fileName);
  const reasons = [];
  if (levels > MAX_PLAUSIBLE_DEPTH_LEVELS) reasons.push(`${levels} depth levels (a model section normally has fewer than ${MAX_PLAUSIBLE_DEPTH_LEVELS})`);
  if (csvLike && model.headerResistivity === false) reasons.push('no column named like resistivity (columns were assigned by position)');
  return reasons.length ? `This file may not be an inversion model: ${reasons.join(' and ')}. Check the section before using it.` : null;
}

/** Display text for one entry of the iteration/RMS history: RMS when present, else chi2 (BERT). */
export function iterationLabel(it) {
  const n = it?.n;
  if (it?.rms != null && it.rms !== '') return `${n} — RMS ${it.rms}%`;
  if (it?.chi2 != null && it.chi2 !== '') return `${n} — chi² ${it.chi2}`;
  return `${n}`;
}

/**
 * Georeference model cells along a straight survey line.
 * @param {object} model - parsed model
 * @param {[number,number]} start - [lon,lat] at x = xRange[0]
 * @param {[number,number]} end   - [lon,lat] at x = xRange[1]
 * @returns cells with lon/lat added
 */
export function georeferenceCells(model, start, end) {
  const [x0, x1] = model.xRange;
  const span = x1 - x0 || 1;
  return model.cells.map(c => {
    const t = (c.x - x0) / span;
    return {
      ...c,
      lon: start[0] + (end[0] - start[0]) * t,
      lat: start[1] + (end[1] - start[1]) * t,
    };
  });
}

/**
 * Formats parsed by the backend plugin registry (eimodels/registry.py) instead of
 * in the browser: EarthImager 1D/2D/3D .out logs, 1D .mod, 3D *_InvRes*.dat.
 */
export function isServerParsedFormat(fileName = '', text = '') {
  const ext = (fileName.split('.').pop() || '').toLowerCase();
  if (ext === 'out' || ext === 'mod' || ext === 'zip') return true;
  if (/invres/i.test(fileName)) return true;
  const head = text.slice(0, 2000);
  // ERImager-BERT triangles.csv / child model.csv (x1,z1,x2,z2,x3,z3,... header)
  if (ext === 'csv' && /^\s*x1(_\w+)?,\s*z1(_\w+)?,\s*x2/i.test(head)) return true;
  // 1D Resistivity Field Data Analysis result JSON (interpret/auto, forward-model)
  if ((ext === 'json' || head.trimStart().startsWith('{'))
    && /"(refined_model|soil_classifications|layers)"/.test(text.slice(0, 400000))) return true;
  return /elemental sequential order/.test(text.slice(0, 400000));
}

/** Binary result bundles (ERImager-BERT export .zip) travel to the backend base64-encoded. */
export async function readFileForServer(file) {
  if (/\.gz$/i.test(file.name || '')) {
    if (typeof DecompressionStream === 'undefined') throw new Error('This browser cannot open .gz files; unzip the file and import the .out.');
    const stream = file.stream().pipeThrough(new DecompressionStream('gzip'));
    return new Response(stream).text();
  }
  if (!/\.zip$/i.test(file.name || '')) return file.text();
  return file.arrayBuffer().then((buf) => {
    const bytes = new Uint8Array(buf);
    let bin = '';
    for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
    return btoa(bin);
  });
}

/**
 * Backend product (POST /api/inversion/parse) -> the normalized model shape above,
 * plus geometryKind (point | line | grid | mesh2d | mesh3d), electrodes, crs,
 * iteration and the RMS history (`iterations`) for the QC charts.
 */
export function productToModel(p, fileName = '') {
  return {
    name: fileName.replace(/\.[^.]+$/, '') || p.format || 'Inversion model',
    format: p.format,
    software: p.software || null,
    nCells: p.n_cells ?? (p.cells || []).length,
    columns: p.columns || ['x', 'depth', 'resistivity'],
    cells: p.cells || [],
    xRange: p.x_range,
    yRange: p.y_range || null,
    depthRange: p.depth_range,
    depths: p.depths || [],
    rmsPercent: p.rms_percent ?? null,
    iterations: (p.iterations || []).map(it => ({ n: it.n, rms: it.rms, l2: it.l2 })),
    iteration: p.iteration ?? null,
    nIterations: p.n_iterations ?? 0,
    doi: p.doi,
    geometryKind: p.geometry_kind || 'line',
    electrodes: p.electrodes || [],
    mesh: p.mesh || null,
    crs: p.crs || 'local',
    georef: p.georef || null,
    dataFit: p.data_fit || null, // measured vs calculated readings (EarthImager .out, BERT data_fit.csv)
  };
}

/** Cells to draw in a 2D section: 3D models are cut at the y row nearest `y` (default: middle). */
export function sectionCells(model, y = null) {
  const cells = model.cells || [];
  if (!['grid', 'mesh3d'].includes(model.geometryKind) || !cells.length) return cells;
  const ys = [...new Set(cells.map(c => c.y))].sort((a, b) => a - b);
  const target = y === null ? ys[Math.floor(ys.length / 2)] : ys.reduce((b, v) => (Math.abs(v - y) < Math.abs(b - y) ? v : b), ys[0]);
  return cells.filter(c => c.y === target);
}

/**
 * Half-gap tolerance around `nearest` using the local layer spacing (layers
 * thicken with depth, so the shallowest spacing under-populates deep slices).
 * Uses the larger of the gaps to the neighbouring levels, times 0.51.
 */
export function localDepthTolerance(depthsAvailable, nearest) {
  const i = depthsAvailable.indexOf(nearest);
  const gaps = [];
  if (i > 0) gaps.push(nearest - depthsAvailable[i - 1]);
  if (i >= 0 && i < depthsAvailable.length - 1) gaps.push(depthsAvailable[i + 1] - nearest);
  const spacing = gaps.length ? Math.max(...gaps) : 1;
  return Math.max(0.51, spacing * 0.51);
}

/**
 * Extract a depth slice: nearest cells to the requested depth, one per x-station.
 * `exclude(cell)` drops cells (e.g. below the DOI) after the depth level is chosen.
 * @returns [{ x, lon?, lat?, value }]
 */
export function extractDepthSlice(cells, depth, property = 'resistivity', tolerance = null, exclude = null) {
  const depthsAvailable = [...new Set(cells.map(c => c.depth))].sort((a, b) => a - b);
  // Snap to the nearest model depth level
  let nearest = depthsAvailable[0];
  depthsAvailable.forEach(d => { if (Math.abs(d - depth) < Math.abs(nearest - depth)) nearest = d; });
  const tol = tolerance ?? localDepthTolerance(depthsAvailable, nearest);
  return cells
    .filter(c => Math.abs(c.depth - nearest) <= tol && typeof c[property] === 'number' && !(exclude && exclude(c)))
    .map(c => ({ x: c.x, lon: c.lon, lat: c.lat, value: c[property], depth: nearest }));
}

/** Log-scale cool→warm color for resistivity values. */
export function resistivityColor(value, logMin, logMax) {
  const lv = Math.log10(Math.max(value, 1e-6));
  const t = Math.max(0, Math.min(1, (lv - logMin) / ((logMax - logMin) || 1)));
  const stops = [
    [37, 99, 235], [6, 182, 212], [52, 211, 153], [251, 191, 36], [251, 146, 60], [248, 113, 113],
  ];
  const pos = t * (stops.length - 1);
  const lo = Math.floor(pos), hi = Math.min(stops.length - 1, lo + 1), fr = pos - lo;
  const rgb = stops[lo].map((c, i) => Math.round(c + (stops[hi][i] - c) * fr));
  return `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;
}
